import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, inject, forwardRef, ViewChild, Output, Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { UntypedFormGroup, Validators, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { E11AutocompleteModule } from '@engineering11/ui-lib/e11-autocomplete';
import * as i5 from '@engineering11/ui-lib/e11-directives';
import { E11DirectivesModule } from '@engineering11/ui-lib/e11-directives';
import * as i3 from '@engineering11/ui-lib/e11-inline-footer';
import { E11InlineFooterModule } from '@engineering11/ui-lib/e11-inline-footer';
import * as i4 from '@engineering11/ui-lib/e11-inline-label';
import { fontSizeMap, E11InlineLabelModule } from '@engineering11/ui-lib/e11-inline-label';
import { E11InputModule } from '@engineering11/ui-lib/e11-input';
import { E11InputErrorsModule } from '@engineering11/ui-lib/e11-input-errors';
import { E11TooltipModule } from '@engineering11/ui-lib/e11-tooltip';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';

class E11InlineInputComponent {
    set value(value) {
        this._value = value;
        this.setDisplayValue();
        this.persistValue();
    }
    constructor(cd) {
        this.cd = cd;
        this.editing = false;
        this.required = false;
        this.loading = false;
        this.editIcon = false;
        this.hasError = false;
        this.showCharCount = false;
        this.size = 'md';
        this.formControlName = '';
        this.parentForm = new UntypedFormGroup({});
        this.inputSaved = new EventEmitter();
        this.inputValue = '';
        this.onChange = () => { };
        this.onTouch = () => { };
        this.disabledClick = false;
        this.labelClicked = false;
        this.fontSizeMap = fontSizeMap;
        this.Validators = Validators;
        this.isBrowser = inject(IS_BROWSER);
    }
    ngOnInit() {
        this.initFormMethods();
    }
    get f() {
        return this.parentForm.controls;
    }
    initFormMethods() {
        if (!this.formControlName || !this.parentForm)
            return;
        this.formControl = this.f[this.formControlName];
        if (!this.formControl)
            return;
        if (this.parentForm.dirty && this.formControl?.errors)
            this.hasError = true;
        if (this.required && !this.formControl.hasValidator(Validators.required))
            this.formControl.addValidators([Validators.required]);
        if (this._value) {
            this.setDisplayValue();
            this.persistValue();
            this.updateFormValue(this._value);
        }
        this.formControl.updateValueAndValidity();
    }
    checkError() {
        if (this.formControl.errors && (this.formControl.touched || this.formControl.dirty)) {
            this.hasError = true;
        }
        else {
            this.hasError = false;
        }
    }
    handleClickOutside() {
        if (this.labelClicked) {
            this.labelClicked = false;
            return;
        }
        this.updateFormValue(this.savedValue);
        this.toggleEditing();
    }
    saveEdit() {
        if (this.hasError) {
            this.disabledClick = true;
            return;
        }
        this._value = this.savedValue = this.textInputElement.nativeElement.value || undefined;
        this.updateFormValue(this._value);
        this.setDisplayValue();
        this.inputSaved.emit(this._value);
        this.toggleEditing();
    }
    cancelEdit() {
        this.updateFormValue(this.savedValue);
        this.toggleEditing();
    }
    toggleEditing(labelClicked = false) {
        if (labelClicked)
            this.labelClicked = true;
        this.editing = !this.editing;
        this.cd.detectChanges();
        this.editing ? this.setFocus() : this.f[this.formControlName].markAsPristine();
    }
    clearInput() {
        this.inputValue = '';
        this.onChange(this.inputValue);
    }
    writeValue(obj) {
        this.inputValue = obj ? obj : '';
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    setFocus() {
        if (this.isBrowser && this.textInputElement) {
            this.textInputElement.nativeElement.focus();
        }
    }
    handleOnChange(event) {
        let updatedValue = event.target.value.trim();
        this.updateFormValue(updatedValue);
    }
    updateFormValue(value) {
        if (this.formControlName && this.f[this.formControlName]) {
            this.f[this.formControlName].setValue(value);
        }
        this.f[this.formControlName].updateValueAndValidity();
        this.checkError();
    }
    setDisplayValue() {
        this.displayValue = this.displayValueFn !== undefined ? this.displayValueFn(this._value) : this._value;
    }
    persistValue() {
        if (this.formControlName && !this.f[this.formControlName]?.dirty) {
            this.savedValue = this.f[this.formControlName].value;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineInputComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11InlineInputComponent, selector: "e11-inline-input", inputs: { label: "label", value: "value", placeholder: "placeholder", editing: "editing", required: "required", loading: "loading", editIcon: "editIcon", hasError: "hasError", showCharCount: "showCharCount", maxLength: "maxLength", size: "size", tooltipConfig: "tooltipConfig", requiredTooltipConfig: "requiredTooltipConfig", formControlName: "formControlName", parentForm: "parentForm", displayValueFn: "displayValueFn" }, outputs: { inputSaved: "inputSaved" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11InlineInputComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "textInputElement", first: true, predicate: ["input"], descendants: true }], ngImport: i0, template: `
    <div [formGroup]="parentForm">
      <ng-container *ngIf="!editing">
        <e11-inline-label
          [label]="label"
          [value]="displayValue"
          [placeholder]="placeholder"
          [loading]="loading"
          [size]="size"
          [hasError]="required ? f[formControlName].errors?.required : undefined"
          [required]="required"
          [editIcon]="true"
          [tooltipConfig]="tooltipConfig"
          [requiredTooltipConfig]="requiredTooltipConfig"
          (labelClicked)="toggleEditing(true)"
        ></e11-inline-label>
      </ng-container>
      <div *ngIf="editing" (closeOnClickOutside)="handleClickOutside()" class="e11-w-full">
        <div class="e11-mb-1" (keyup)="checkError()">
          <input
            #input
            [formControlName]="formControlName"
            [placeholder]="placeholder ?? ''"
            [attr.maxLength]="maxLength"
            [type]="'text'"
            (change)="handleOnChange($event)"
            (keyup.enter)="saveEdit()"
            class="e11-bg-transparent e11-px-1 e11-py-0 {{ fontSizeMap[size] }} e11-border-skin-app-borders e11-w-full"
            [ngClass]="{ 'e11-border-skin-error e11-text-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error': hasError }"
          />
        </div>
        <div class="e11-flex e11-justify-end">
          <div class="e11-absolute">
            <div class="e11-flex e11-items-center">
              <ng-container *ngIf="maxLength || showCharCount">
                <div class="e11-flex e11-justify-end e11-text-xs e11-text-skin-light e11-mr-2">
                  <span class="">{{ f[formControlName].value?.length ?? 0 }} / {{ maxLength }}</span>
                </div>
              </ng-container>
              <e11-inline-footer [confirmDisabled]="hasError" (confirm)="saveEdit()" (cancel)="cancelEdit()"></e11-inline-footer>
            </div>
          </div>
        </div>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: i3.E11InlineFooterComponent, selector: "e11-inline-footer", inputs: ["confirmIcon", "cancelIcon", "confirmDisabled"], outputs: ["confirm", "cancel"] }, { kind: "component", type: i4.E11InlineLabelComponent, selector: "e11-inline-label", inputs: ["label", "value", "placeholder", "size", "loading", "hasError", "required", "editIcon", "tooltipConfig", "requiredTooltipConfig"], outputs: ["labelClicked"] }, { kind: "directive", type: i5.ClickOutsideDirective, selector: "[closeOnClickOutside]", outputs: ["closeOnClickOutside"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineInputComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-inline-input',
                    template: `
    <div [formGroup]="parentForm">
      <ng-container *ngIf="!editing">
        <e11-inline-label
          [label]="label"
          [value]="displayValue"
          [placeholder]="placeholder"
          [loading]="loading"
          [size]="size"
          [hasError]="required ? f[formControlName].errors?.required : undefined"
          [required]="required"
          [editIcon]="true"
          [tooltipConfig]="tooltipConfig"
          [requiredTooltipConfig]="requiredTooltipConfig"
          (labelClicked)="toggleEditing(true)"
        ></e11-inline-label>
      </ng-container>
      <div *ngIf="editing" (closeOnClickOutside)="handleClickOutside()" class="e11-w-full">
        <div class="e11-mb-1" (keyup)="checkError()">
          <input
            #input
            [formControlName]="formControlName"
            [placeholder]="placeholder ?? ''"
            [attr.maxLength]="maxLength"
            [type]="'text'"
            (change)="handleOnChange($event)"
            (keyup.enter)="saveEdit()"
            class="e11-bg-transparent e11-px-1 e11-py-0 {{ fontSizeMap[size] }} e11-border-skin-app-borders e11-w-full"
            [ngClass]="{ 'e11-border-skin-error e11-text-skin-error focus:e11-ring-skin-error focus:e11-border-skin-error': hasError }"
          />
        </div>
        <div class="e11-flex e11-justify-end">
          <div class="e11-absolute">
            <div class="e11-flex e11-items-center">
              <ng-container *ngIf="maxLength || showCharCount">
                <div class="e11-flex e11-justify-end e11-text-xs e11-text-skin-light e11-mr-2">
                  <span class="">{{ f[formControlName].value?.length ?? 0 }} / {{ maxLength }}</span>
                </div>
              </ng-container>
              <e11-inline-footer [confirmDisabled]="hasError" (confirm)="saveEdit()" (cancel)="cancelEdit()"></e11-inline-footer>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11InlineInputComponent),
                            multi: true,
                        },
                    ],
                    encapsulation: ViewEncapsulation.None,
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { label: [{
                type: Input
            }], value: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], editing: [{
                type: Input
            }], required: [{
                type: Input
            }], loading: [{
                type: Input
            }], editIcon: [{
                type: Input
            }], hasError: [{
                type: Input
            }], showCharCount: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], size: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], requiredTooltipConfig: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], displayValueFn: [{
                type: Input
            }], inputSaved: [{
                type: Output
            }], textInputElement: [{
                type: ViewChild,
                args: ['input', { static: false }]
            }] } });

class E11InlineInputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11InlineInputModule, declarations: [E11InlineInputComponent], imports: [CommonModule,
            ReactiveFormsModule,
            E11TooltipModule,
            E11InputModule,
            E11AutocompleteModule,
            E11InlineFooterModule,
            E11InlineLabelModule,
            E11InputErrorsModule,
            E11DirectivesModule,
            E11UtilModule], exports: [E11InlineInputComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineInputModule, imports: [CommonModule,
            ReactiveFormsModule,
            E11TooltipModule,
            E11InputModule,
            E11AutocompleteModule,
            E11InlineFooterModule,
            E11InlineLabelModule,
            E11InputErrorsModule,
            E11DirectivesModule,
            E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineInputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11InlineInputComponent],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        E11TooltipModule,
                        E11InputModule,
                        E11AutocompleteModule,
                        E11InlineFooterModule,
                        E11InlineLabelModule,
                        E11InputErrorsModule,
                        E11DirectivesModule,
                        E11UtilModule,
                    ],
                    exports: [E11InlineInputComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11InlineInputComponent, E11InlineInputModule };
